<script lang="ts">import { stateUi } from "@infinitro/config";
import { ArrowIcon } from "@infinitro/icons";
import { GradientBorder } from "@infinitro/info-card";
import { onMount, tick } from "svelte";
export let title = "";
export let text = "";
export let additionalContent = "";
export let isHtml = false;
export let open = false;
export let rightTitle = "";
export let panelPadding = "1rem";
export let expandedBackground = false;
export let onToggle = undefined;
let panel;
let panelContent;
let panelMaxHeight = "0px";
const updatePanelMaxHeight = async (isOpen = open) => {
	await tick();
	if (isOpen !== open) return;
	panelMaxHeight = isOpen && panel ? `${panel.scrollHeight}px` : "0px";
};
const toggle = () => {
	if (onToggle) {
		onToggle(!open);
		return;
	}
	if (open) {
		open = false;
	} else {
		open = true;
	}
};
$: useAlternative4 = $stateUi.accordion?.accordionStyle === "alternative4";
$: useExpandedBackground = expandedBackground || ($stateUi.accordion?.accordionExpandedBackground ?? false);
$: void updatePanelMaxHeight(open);
onMount(() => {
	if (typeof ResizeObserver === "undefined" || !panelContent) return;
	const observer = new ResizeObserver(() => {
		if (open) void updatePanelMaxHeight();
	});
	observer.observe(panelContent);
	return () => observer.disconnect();
});
</script>

<GradientBorder
	radius={12}
	gradient="linear-gradient(180deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.06) 38%, rgba(255,255,255,0) 100%)"
	isEnabled={useAlternative4 ? false : true}
>
	<div class="acc2" class:open class:expanded-bg={useExpandedBackground}>
		<div class="surface" class:surface-alternative-4={useAlternative4}>
			<!-- svelte-ignore a11y-click-events-have-key-events -->
			<!-- svelte-ignore a11y-no-static-element-interactions -->
			<button class="header" type="button" on:click={toggle} aria-expanded={open}>
				<p class="title">{title}</p>
				<div class="right-title-arrow-container">
					{#if rightTitle}
						{@html rightTitle}
					{/if}
					<span class="chev" class:rotated={open}>
						<ArrowIcon />
					</span>
				</div>
			</button>

			<div
				bind:this={panel}
				class="panel"
				class:expanded={open}
				style={`max-height: ${panelMaxHeight};`}
			>
				<div
					bind:this={panelContent}
					class:content-visible={open}
					class="panel-content"
					style={`padding: ${panelPadding};`}
				>
					{#if isHtml && text}
						<p class="text">{@html text}</p>
					{:else if text}
						<p class="text">{text}</p>
					{/if}

					{#if additionalContent}
						<div class="additional">{@html additionalContent}</div>
					{/if}

					<slot name="content" />
				</div>
			</div>
		</div>
	</div>
</GradientBorder>

<style>
	.acc2 {
		width:
			100%;
		overflow:
			hidden;
		border-radius:
			12px;
}

	.surface {
		background:
			radial-gradient(
				100% 1500% at 85% 50%,
				rgba(255, 255, 255, 0.06) 0%,
				rgba(255, 255, 255, 0.04) 35%,
				rgba(255, 255, 255, 0.02) 55%,
				rgba(255, 255, 255, 0) 70%
			),
			linear-gradient(90deg, var(--color-tertiary-300) 0%, var(--color-tertiary-700) 100%);
	}

	.surface-alternative-4 {
		background-color:
			var(--color-background-500);
		background-image: unset;
}

	.header {
		display:
			flex;
		min-height:
			54px;
		width:
			100%;
		align-items:
			center;
		justify-content:
			space-between;
		padding-left:
			1rem;
		padding-right:
			1rem;
		text-align:
			left;
		background-color:
			transparent;
}

	.title {
		font-size:
			0.875rem;
		line-height:
			1.25rem;
		font-weight:
			600;
		color:
			var(--color-text-100);
		letter-spacing:
			0.3px;
}

	.chev {
		display:
			inline-flex;
		align-items:
			center;
		justify-content:
			center;
		opacity:
			0.3;
		height:
			2rem;
		width:
			2rem;
		border-radius:
			9999px;
		transition: transform 220ms ease;
}

	.chev.rotated {
		transform: rotate(180deg);
	}

	.panel {
		overflow:
			hidden;
		max-height: 0;
		transition: max-height 320ms ease;
}

	.acc2:not(.expanded-bg) .panel {
		background: var(--color-background-500);
	}

	.acc2.expanded-bg .panel {
		background: transparent;
	}

	.text {
		padding-top:
			0.5rem;
		padding-bottom:
			1rem;
		font-size:
			0.875rem;
		font-weight:
			400;
		color:
			var(--color-text-300);
		line-height:
			1.5rem;
}

	.additional {
		padding-bottom:
			1rem;
}

	.panel-content {
		width:
			100%;
		opacity: 0;
		transition: opacity 180ms ease;
}

	.panel-content.content-visible {
		opacity: 1;
	}

	.right-title-arrow-container {
		display:
			flex;
		align-items:
			center;
		justify-content:
			flex-end;
		gap:
			0.5rem;
}

	@media (prefers-reduced-motion: reduce) {
		.panel,
		.panel-content,
		.chev {
			transition: none;
		}
	}
</style>
